import { Badge, IconButton, Price } from '@artosera/ui';
import { formatDate } from '@artosera/shared';
import { Archive, ChevronLeft, ChevronRight, FileText, Lock } from 'lucide-react';
import type { JSX } from 'react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { epoqueCourante, useIdentite } from '../../auth/identite.js';
import { FacturesAnterieures } from './FacturesAnterieures.js';
import { useArchiveFactures } from '../../api/archiveFactures.js';
import type { FacturePresentee } from '../../api/ventes.js';
import { estApiError } from '../../api/errors.js';
import { ExportFec } from '../reglages/ExportFec.js';
import { galerieALaction, galerieAModule } from '../../auth/sujet.js';
import { useShellContext } from '../shell/context.js';
import { teinteDeLEtatFacture } from '../ventes/etatUi.js';
import styles from './ArchiveFactures.module.css';

/**
 * Archive des factures (D21) — les factures et avoirs ÉMIS, en LECTURE SEULE,
 * servis par le routeur de conservation légale (`…/archive-factures`, module du
 * cœur) : c'est ce que la galerie garde quand le module `ventes` est coupé.
 * Aucune action : ni création, ni règlement, ni avoir.
 */
export function ArchiveFacturesPage(): JSX.Element {
  const { t, i18n } = useTranslation(['regie', 'ventes', 'commun']);
  const langue = i18n.language === 'en' ? 'en' : 'fr';
  const { galerieSlug, galerie } = useShellContext();
  const identite = useIdentite();
  const portee = `${identite?.compteId ?? ''}:${galerieSlug}:${epoqueCourante()}`;
  const [page, setPage] = useState(1);
  const peutLire = galerieALaction(galerie, 'invoices:read');
  const liste = useArchiveFactures(galerieSlug, page);

  const factures = liste.data?.factures ?? [];
  const pagination = liste.data?.pagination;

  // Les pièces se rangent par année d'émission, comme des classeurs : l'ordre
  // reçu de l'API est conservé à l'intérieur de chaque année.
  const parAnnee = useMemo(() => {
    const classeurs = new Map<string, FacturePresentee[]>();
    for (const facture of liste.data?.factures ?? []) {
      const annee = facture.emiseLe ? facture.emiseLe.slice(0, 4) : '—';
      const classeur = classeurs.get(annee) ?? [];
      classeur.push(facture);
      classeurs.set(annee, classeur);
    }
    return [...classeurs.entries()];
  }, [liste.data]);

  return (
    <div className={styles.page}>
      <header className={styles.enteteArchive}>
        <span className={styles.boite} aria-hidden>
          <Archive size={22} strokeWidth={1.5} />
        </span>
        <div className={styles.enteteTexte}>
          <span className={styles.etiquette}>
            <Lock size={12} strokeWidth={2} aria-hidden />
            {t('regie:archiveFactures.lectureSeule')}
          </span>
          <h1 className={styles.titre}>{t('regie:archiveFactures.titre')}</h1>
          <p className={styles.sousTitre}>{t('regie:archiveFactures.sousTitre')}</p>
        </div>
        {galerieAModule(galerie, 'api-comptabilite') &&
        galerieALaction(galerie, 'invoices:export') ? (
          <ExportFec galerie={galerieSlug} />
        ) : null}
      </header>

      {!peutLire ? (
        <p className={styles.vide}>
          <Lock size={20} strokeWidth={1.5} aria-hidden />
          {t('regie:archiveFactures.refus')}
        </p>
      ) : liste.isLoading ? (
        <p className={styles.vide}>{t('commun:chargement')}</p>
      ) : liste.isError ? (
        <p className={styles.vide} role="alert">
          {estApiError(liste.error) ? liste.error.message : t('commun:erreurReseau')}
        </p>
      ) : factures.length === 0 ? (
        <div className={styles.videClasseur}>
          <span className={styles.classeurVide} aria-hidden />
          <p>{t('regie:archiveFactures.aucune')}</p>
        </div>
      ) : (
        parAnnee.map(([annee, pieces]) => (
          <section key={annee} className={styles.classeur} aria-labelledby={`archive-${annee}`}>
            <h2 className={styles.classeurAnnee} id={`archive-${annee}`}>
              {annee}
            </h2>
            <div className={styles.tableauEnveloppe}>
              <table className={styles.tableau} data-density="registre">
                <thead>
                  <tr>
                    <th scope="col">{t('regie:ventes.colonneNumero')}</th>
                    <th scope="col">{t('regie:ventes.colonneDate')}</th>
                    <th scope="col">{t('regie:ventes.colonneContact')}</th>
                    <th scope="col" className={styles.colMontant}>
                      {t('regie:ventes.colonneTtc')}
                    </th>
                    <th scope="col">{t('regie:ventes.colonneEtat')}</th>
                    <th scope="col">{t('regie:ventes.colonneDocuments')}</th>
                  </tr>
                </thead>
                <tbody>
                  {pieces.map((facture) => (
                    <tr key={facture.id}>
                      <td className={styles.celNumero}>
                        <Link
                          className={styles.lienLigne}
                          to={`/${galerieSlug}/archive-factures/${facture.id}`}
                        >
                          <span className={styles.numero}>{facture.numero ?? '—'}</span>
                          {facture.type === 'avoir' ? (
                            <Badge tone="neutral">{t('ventes:factures.avoir')}</Badge>
                          ) : null}
                        </Link>
                      </td>
                      <td className={styles.celDate}>
                        {facture.emiseLe
                          ? formatDate(new Date(facture.emiseLe), langue, 'court')
                          : '—'}
                      </td>
                      <td className={styles.celContact}>{facture.contactNom ?? '—'}</td>
                      <td className={`${styles.colMontant} ${styles.celTtc}`}>
                        <Price
                          cents={facture.totaux.totalTtcCentimes}
                          lang={langue}
                          currency={facture.devise}
                        />
                      </td>
                      <td className={styles.celEtat}>
                        <Badge tone={teinteDeLEtatFacture(facture.etat)}>
                          {t(`ventes:factures.${facture.etat}`)}
                        </Badge>
                      </td>
                      <td className={styles.celDocuments}>
                        <span className={styles.documents}>
                          {facture.pdfDisponible ? (
                            <span className={styles.pastilleDoc}>
                              <FileText
                                size={14}
                                strokeWidth={1.5}
                                aria-label={t('regie:ventes.pdfDisponible')}
                              />
                            </span>
                          ) : null}
                          {facture.facturXDisponible ? (
                            <span className={styles.facturX}>Factur-X</span>
                          ) : null}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        ))
      )}

      {pagination && pagination.pages > 1 ? (
        <div className={styles.pagination}>
          <IconButton
            variant="outline"
            icon={<ChevronLeft aria-hidden />}
            aria-label={t('regie:ventes.pagination.precedent')}
            size="sm"
            disabled={page <= 1}
            onClick={() => setPage((valeur) => Math.max(1, valeur - 1))}
          />
          <span>
            {t('regie:ventes.pagination.page', { page: pagination.page, pages: pagination.pages })}
          </span>
          <IconButton
            variant="outline"
            icon={<ChevronRight aria-hidden />}
            aria-label={t('regie:ventes.pagination.suivant')}
            size="sm"
            disabled={page >= pagination.pages}
            onClick={() => setPage((valeur) => Math.min(pagination.pages, valeur + 1))}
          />
        </div>
      ) : null}
      {peutLire ? <FacturesAnterieures key={portee} galerieSlug={galerieSlug} /> : null}
    </div>
  );
}
